// web/src/components/TournamentsPage.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "./PageLayout";
import { Account } from "./TopMenuBar";
import TournamentBracketModal, { TournamentBracketData } from "./TournamentBracketModal";
import { useViewport } from "../hooks/useViewport";
import { ANDROID_TABLET_MENU_OFFSET, ANDROID_TABLET_SCROLL_REDUCTION } from "../lib/androidLayout";

const API_BASE = (import.meta as any).env?.VITE_API_BASE || "http://localhost:8100";
const LEADERBOARD_BG = "LeaderboardBackground.png";

function getAssetPath(path: string): string {
  const cleanPath = path.replace(/^\//, "");
  const isBrowser = typeof window !== "undefined";

  const cap = isBrowser ? (window as any).Capacitor : null;
  const isNative =
    Boolean(cap) &&
    (typeof cap.isNativePlatform === "function"
      ? cap.isNativePlatform()
      : typeof cap.getPlatform === "function"
        ? cap.getPlatform() === "ios" || cap.getPlatform() === "android"
        : cap.platform === "ios" || cap.platform === "android");

  if (isNative) {
    try {
      const baseUrl = new URL(window.location.href);
      return baseUrl.origin + "/" + cleanPath;
    } catch (e) {
      console.warn("[getAssetPath] Failed to construct URL, using fallback:", e);
      return "/" + cleanPath;
    }
  }

  return "./" + cleanPath;
}

interface Tournament {
  id: number;
  name: string;
  tier: string;
  format_type: string;
  gear_cost: number;
  banked_glint_requirement: string;
  scheduled_start_time: string | null;
  scheduled_end_time: string | null;
  registration_open_time: string | null;
  registration_close_time: string | null;
  status: string;
  cancellation_reason?: string | null;
  config: {
    min_players?: number;
    max_players?: number;
    prize_table?: { [placement: string]: { glint?: number; gear?: number } };
    best_of_settings?: { [key: string]: number };
  };
  entry_count?: number;
  max_players?: number;
  current_prize_table?: { [placement: string]: { glint?: number; gear?: number } } | null; // Dynamically calculated prizes based on current registrations
  user_entry?: {
    id: number;
    status: string;
    final_placement?: number;
    prizes?: { glint?: number; gear?: number };
  } | null;
}

interface TournamentsPageProps {
  account: Account;
  onAccountRefresh?: () => void;
  onLogout?: () => void;
}

type TabType = "active" | "upcoming" | "past";

export default function TournamentsPage({ account, onAccountRefresh, onLogout }: TournamentsPageProps) {
  const navigate = useNavigate();
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  const isAndroid = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'android';
  const isIOSApp = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform && (window as any).Capacitor.getPlatform() === 'ios';
  
  // Bulletproof iPhone detection - exclude iPad (must be after viewport declaration)
  const isIPhone = (() => {
    if (!isIOSApp) return false;
    const userAgent = typeof navigator !== 'undefined' ? (navigator.userAgent || navigator.vendor || '').toLowerCase() : '';
    const platform = typeof navigator !== 'undefined' ? (navigator as any).platform || '' : '';
    const maxTouchPoints = typeof navigator !== 'undefined' ? (navigator as any).maxTouchPoints || 0 : 0;
    const maxDim = Math.max(viewport.width, viewport.height);
    const minDim = Math.min(viewport.width, viewport.height);
    
    // Explicit iPad in user agent
    if (userAgent.includes('ipad')) return false;
    
    // Modern iPadOS 13+ reports as MacIntel with touch support
    if (platform.toLowerCase() === 'macintel' && maxTouchPoints > 1 && maxDim >= 768 && minDim >= 768) return false;
    
    // If dimensions suggest iPad, exclude it
    if (maxDim >= 768 && minDim >= 768) return false;
    
    // Explicit iPhone in user agent (but not iPad)
    if (userAgent.includes('iphone') && !userAgent.includes('ipad')) return true;
    
    // iPod touch counts as iPhone-like
    if (userAgent.includes('ipod')) return true;
    
    // If it's iOS app and not iPad (by dimensions), assume iPhone
    if (isIOSApp && (maxDim < 768 || minDim < 768)) return true;
    
    return false;
  })();
  
  // Bulletproof iPad detection - exclude iPhone (must be after isIPhone declaration)
  const isIOSPad = (() => {
    if (!isIOSApp) return false;
    if (isIPhone) return false; // If it's iPhone, it's not iPad
    return Math.max(viewport.width, viewport.height) >= 768;
  })();
  const isAndroidTablet = viewport.isAndroidTablet;
  const androidInsetLeft = isAndroid ? Math.round(viewport.width * (70 / 1920)) : 0;
  const androidInsetRight = isAndroid ? Math.round(viewport.width * (112 / 1920)) : 0;
  const androidMenuOffset = isAndroidTablet ? ANDROID_TABLET_MENU_OFFSET : 0;
  const scrollReduction = isAndroidTablet ? ANDROID_TABLET_SCROLL_REDUCTION : 0;
  const textScale = isAndroidTablet ? 3.6 : (isIOSPad ? 1.18 : 1); // slightly larger main content on iPad
  const androidContentShift = isAndroidTablet ? 25 : 0;
  const androidBodyFontSize = isAndroidTablet ? '18px' : undefined;
  const androidBodyLineHeight = isAndroidTablet ? 1.3 : undefined;
  const iosPadGutterX = isIOSPad ? Math.round(viewport.width * 0.05) : 0;
  const iosPadGutterY = isIOSPad ? Math.round(viewport.height * 0.05) : 0;
  const iosPadShiftX = isIOSPad ? 10 : 0;
  const iosPadShiftY = isIOSPad ? 24 : 0;
  const backgroundSrc = (isIOSPad || isIPhone) ? "TabletBackground.png" : LEADERBOARD_BG;
  // iPhone-specific adjustments: move right and make narrower (matching Leaderboard)
  const iphoneRightShift = isIPhone ? 10 : 0; // Move right by 10px (keeps left side in place)
  const iphoneRightNarrow = isIPhone ? 27 : 0; // Add 27px to right to make narrower
  const androidTextStyle = isAndroidTablet ? (
    <style>{`
      .android-tablet-text-tournaments, .android-tablet-text-tournaments * {
        font-size: ${androidBodyFontSize};
        line-height: ${androidBodyLineHeight};
      }
    `}</style>
  ) : null;
  const iosTopPad = isIOSPad ? 78 : 0;
  const iosBottomPad = isIOSPad ? 120 : 0;
  const [activeTab, setActiveTab] = useState<TabType>("active");
  const contentRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [showTopIndicator, setShowTopIndicator] = useState(false);
  const [showBottomIndicator, setShowBottomIndicator] = useState(false);

  // Scroll to top on mount for mobile landscape
  React.useEffect(() => {
    if (isMobileLandscape && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [isMobileLandscape]);


  const updateScrollIndicators = (el: HTMLDivElement | null) => {
    if (!el) return;
    const isAtTop = el.scrollTop === 0;
    const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 1;
    setShowTopIndicator(!isAtTop);
    setShowBottomIndicator(!isAtBottom);
  };
  const [tournaments, setTournaments] = useState<{ active: Tournament[]; upcoming: Tournament[]; past: Tournament[] }>({ active: [], upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enteringTournament, setEnteringTournament] = useState<number | null>(null);
  const [currentMatches, setCurrentMatches] = useState<{ [tournamentId: number]: any }>({});
  const [pastLoading, setPastLoading] = useState(false);
  const [pastLoaded, setPastLoaded] = useState(false);
  const [pastHasMore, setPastHasMore] = useState(true);
  const [pastOffset, setPastOffset] = useState(0);
  const [pastError, setPastError] = useState<string | null>(null);
  const [pastCount, setPastCount] = useState<number | null>(null);
  const [autoStartedMatches, setAutoStartedMatches] = useState<Set<number>>(new Set());
  const [cancellationTimestamps, setCancellationTimestamps] = useState<Map<number, number>>(new Map());
  const [bracketModalOpen, setBracketModalOpen] = useState(false);
  const [selectedBracketTournament, setSelectedBracketTournament] = useState<Tournament | null>(null);
  const [bracketData, setBracketData] = useState<TournamentBracketData | null>(null);
  const [bracketLoading, setBracketLoading] = useState(false);
  const [bracketError, setBracketError] = useState<string | null>(null);
  const PAST_PAGE_SIZE = 20;

  useEffect(() => {
    if (scrollRef.current) {
      requestAnimationFrame(() => updateScrollIndicators(scrollRef.current));
    }
  }, [activeTab, tournaments, isMobileLandscape]);

  const buildAuthHeaders = async (): Promise<HeadersInit> => {
    const headers: HeadersInit = { "Content-Type": "application/json" };
    let regularUserToken: string | null = null;
    if (typeof window !== "undefined") {
      regularUserToken = localStorage.getItem("regular_user_jwt");
    }
    if (regularUserToken) {
      headers["Authorization"] = `Bearer ${regularUserToken}`;
      return headers;
    }
    // Fallback: use guest token if available (works for guests and as a safe default for signed-in users when JWT is missing)
    try {
      const { getValidGuestToken } = await import("../lib/guest-auth");
      const guestToken = await getValidGuestToken();
      if (guestToken) {
        headers["Authorization"] = `Bearer ${guestToken}`;
      }
    } catch {
      // No token available; continue without Authorization header (session cookie only)
    }
    return headers;
  };

  const fetchTournaments = async (showLoading: boolean = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      
      const headers = await buildAuthHeaders();

      const [activeResponse, upcomingResponse] = await Promise.all([
        fetch(`${API_BASE}/tournaments?category=active`, {
          credentials: "include",
          headers,
        }),
        fetch(`${API_BASE}/tournaments?category=upcoming`, {
          credentials: "include",
          headers,
        })
      ]);

      if (!activeResponse.ok || !upcomingResponse.ok) {
        throw new Error("Failed to fetch tournaments");
      }
      
      const activeData = await activeResponse.json();
      const upcomingData = await upcomingResponse.json();

      let activeTournaments: Tournament[] = activeData.tournaments || [];
      let upcomingTournaments: Tournament[] = upcomingData.tournaments || [];
            
      // Debug: Log prize table data
      console.log('[TOURNAMENTS] Upcoming tournaments data:', upcomingTournaments.map(t => ({
        id: t.id,
        name: t.name,
        tier: t.tier,
        status: t.status,
        has_current_prize_table: !!t.current_prize_table,
        current_prize_table: t.current_prize_table,
        config_prize_table: t.config.prize_table
      })));

      // Sort by start time for display consistency
      activeTournaments.sort((a: Tournament, b: Tournament) => {
        const timeA = a.scheduled_start_time ? new Date(a.scheduled_start_time).getTime() : 0;
        const timeB = b.scheduled_start_time ? new Date(b.scheduled_start_time).getTime() : 0;
        return timeA - timeB;
      });
      
      upcomingTournaments.sort((a: Tournament, b: Tournament) => {
        const timeA = a.registration_open_time ? new Date(a.registration_open_time).getTime() : 0;
        const timeB = b.registration_open_time ? new Date(b.registration_open_time).getTime() : 0;
        return timeA - timeB;
      });
      
      // Check if any tournaments just transitioned to "in_progress"
      // We need to fetch their match data immediately so the button can update without waiting for next poll
      const prevActive = tournaments.active || [];
      const newlyInProgress = activeTournaments.filter((t: Tournament) => 
        t.status === "in_progress" && 
        t.user_entry &&
        !prevActive.find((prev: Tournament) => prev.id === t.id && prev.status === "in_progress")
      );
      
      // Update tournament state while preserving existing tournaments to prevent tiles from disappearing
      // Start from prev.active, merge in new backend data, only remove tournaments that are truly done
      setTournaments(prev => {
        // 1. Start from previous active tournaments (preserves tiles during transitions)
        const activeMap = new Map(prev.active.map(t => [t.id, t]));
        
        // 2. Merge in new active tournaments from backend
        for (const newTournament of activeTournaments) {
          const existing = activeMap.get(newTournament.id);
          
          if (existing) {
            // Always merge to preserve object reference and prevent React remounting
            // This ensures the tile doesn't disappear when tournament data is updated

            activeMap.set(newTournament.id, { ...existing, ...newTournament });
          } else {
            // New tournament - add it
            activeMap.set(newTournament.id, newTournament);
          }
        }
        
        // 3. Prune tournaments that are truly done (completed/cancelled)
        // Use timestamps to distinguish temporary gaps from truly dead tournaments
        const now = Date.now();
        const graceMs = 5 * 60 * 1000; // 5-minute grace period
        const CANCELLED_VISIBILITY_MS = 5 * 60 * 1000; // 5 minutes for cancelled tournaments

        // Track cancellation timestamps for newly cancelled tournaments
        setCancellationTimestamps(prev => {
          const updated = new Map(prev);
          for (const newTournament of activeTournaments) {
            if (newTournament.status === "cancelled" && newTournament.user_entry) {
              // If this tournament just became cancelled, record the timestamp
              if (!updated.has(newTournament.id)) {
                updated.set(newTournament.id, now);
              }
            }
          }
          return updated;
        });

        for (const [id, t] of activeMap) {
          const backendTournament = activeTournaments.find(nt => nt.id === id);
          const stillActiveOnBackend = !!backendTournament;
          
          // Handle cancelled tournaments - keep them visible for 5 minutes
          if (t.status === "cancelled" || backendTournament?.status === "cancelled") {
            const cancelledTournament = backendTournament || t;
            const cancellationTime = cancellationTimestamps.get(id);
            
            // If we have a cancellation timestamp and it's been more than 5 minutes, remove it
            if (cancellationTime && now - cancellationTime > CANCELLED_VISIBILITY_MS) {
              activeMap.delete(id);
              setCancellationTimestamps(prev => {
                const updated = new Map(prev);
                updated.delete(id);
                return updated;
              });
              continue;
            }
            
            // If cancelled tournament is on backend, keep it (it's within the 5-minute window)
            if (backendTournament?.status === "cancelled") {
              continue;
            }
            
            // If cancelled tournament is not on backend but we have a timestamp, check if still within window
            if (cancellationTime && now - cancellationTime <= CANCELLED_VISIBILITY_MS) {
              continue; // Keep it visible
            }
            
            // Otherwise, remove it

            activeMap.delete(id);
            setCancellationTimestamps(prev => {
              const updated = new Map(prev);
              updated.delete(id);
              return updated;
            });
            continue;
          }
          
          // Remove if backend explicitly says it's completed (but not cancelled - handled above)
          if (backendTournament && backendTournament.status === "completed") {
            activeMap.delete(id);
            continue;
          }
          
          // Remove if tournament is completed in our state and not in backend
          if (!stillActiveOnBackend && t.status === "completed") {
            activeMap.delete(id);
            continue;
          }
          
          // Remove if tournament is no longer in backend response AND user is eliminated
          // (This means the tournament completed and user was eliminated)
          if (!stillActiveOnBackend && t.user_entry?.status === "eliminated") {
            activeMap.delete(id);
            continue;
          }

          // Remove if tournament is no longer in backend response AND user entry is disqualified
          // (This means the tournament was cancelled - all players eliminated, no min players, etc.)
          if (!stillActiveOnBackend && t.user_entry?.status === "disqualified") {
            activeMap.delete(id);
            continue;
          }

          // Handle "never started / cancelled after no players" case
          // If backend no longer lists it, AND we're clearly past its registration window,
          // and it's not an in-progress tournament that the user is actively in,
          // then we can safely drop it.
          if (!stillActiveOnBackend) {
            const regClose = t.registration_close_time ? new Date(t.registration_close_time).getTime() : null;
            const startTime = t.scheduled_start_time ? new Date(t.scheduled_start_time).getTime() : null;
            
            // For tournaments that never started (not in_progress, no user entry):
            // If past start time and not in backend, they're cancelled - remove quickly
            const cancelledGraceMs = 60 * 1000; // 1 minute for cancelled tournaments
            const isCancelled = t.status !== "in_progress" && 
                                !t.user_entry &&
                                startTime !== null && 
                                now > startTime + cancelledGraceMs;
            
            // For tournaments that were in progress, use 2-minute grace period
            const inProgressGraceMs = 2 * 60 * 1000; // 2 minutes for in-progress tournaments
            const inProgressDead = t.status === "in_progress" && 
                                   startTime !== null && 
                                   now > startTime + inProgressGraceMs;
            
            // For tournaments that never started (registration closed, past start), use 5-minute grace
            const pastRegWindow = regClose !== null && now > regClose + graceMs;
            const pastStartWindow = startTime !== null && now > startTime + graceMs;
            const clearlyDead = pastRegWindow || pastStartWindow;
            
            // Remove if:
            // - Cancelled tournament (past start, not in backend, not in_progress, no entry) - 1 min grace
            // - In-progress tournament that disappeared - 2 min grace  
            // - Tournament past registration/start window - 5 min grace
            if ((isCancelled || inProgressDead || clearlyDead) && 
                !(t.status === "in_progress" && t.user_entry && !inProgressDead)) {

              // This covers:
              // - min players not met => scheduler cancelled => backend stopped listing it
              // - no one joined => same as above
              // - all players eliminated/timeout => tournament cancelled => backend stopped listing it
              // while still not killing freshly transitioning in_progress tournaments.
              activeMap.delete(id);
              continue;
            }
          }
          
          // For everything else:
          // - If it's missing *temporarily* from backend (classification / timing gap),
          //   but we're still within reg/start window,
          //   keep it in activeMap so the tile doesn't flicker.
        }
        
        // 4. Convert back to array, maintaining order from backend
        // Use backend order for tournaments that exist there, append preserved ones at end
        const backendIds = new Set(activeTournaments.map(t => t.id));
        const orderedActive = [
          ...activeTournaments.map(t => activeMap.get(t.id) || t),
          ...Array.from(activeMap.values()).filter(t => !backendIds.has(t.id))
        ];
        
        return {
          active: orderedActive,
          upcoming: upcomingTournaments,
          past: prev.past,
        };
      });
      
      // Fetch current matches for in-progress tournaments that the user has entered
      // Prioritize newly transitioned tournaments to fetch their match data immediately
      const tournamentsToFetch = activeTournaments.filter((t: Tournament) => 
        t.status === "in_progress" && t.user_entry
      );
      
      // Sort so newly in-progress tournaments are fetched first
      tournamentsToFetch.sort((a, b) => {
        const aIsNew = newlyInProgress.some(t => t.id === a.id);
        const bIsNew = newlyInProgress.some(t => t.id === b.id);
        if (aIsNew && !bIsNew) return -1;
        if (!aIsNew && bIsNew) return 1;
        return 0;
      });
      
      const matchPromises = tournamentsToFetch.map(async (t: Tournament) => {
          try {
            const matchHeaders = await buildAuthHeaders();
            
            const matchResponse = await fetch(`${API_BASE}/tournaments/${t.id}/my-match`, {
              credentials: "include",
              headers: matchHeaders,
            });
            if (matchResponse.ok) {
              const matchData = await matchResponse.json();
              console.log(`[Tournaments] Tournament ${t.id} match data:`, matchData);
              return { tournamentId: t.id, match: matchData.match };
            } else if (matchResponse.status === 404) {
              // No match yet - this is expected for tournaments that just started
              console.log(`[Tournaments] Tournament ${t.id} - no match yet (404), showing "Preparing match..."`);
              return { tournamentId: t.id, match: null };
            } else {
              console.log(`[Tournaments] Tournament ${t.id} match fetch failed:`, matchResponse.status);
            }
          } catch (err) {
            console.error(`[Tournaments] Error fetching match for tournament ${t.id}:`, err);
            // Ignore errors for individual match fetches
          }
          return { tournamentId: t.id, match: null };
        });
      
      const matchResults = await Promise.all(matchPromises);
      const matchesMap: { [key: number]: any } = {};
      matchResults.forEach(({ tournamentId, match }) => {
        // Only store matches that exist - don't store null values
        // This prevents errors when accessing match.id on null values
        if (match) {
          matchesMap[tournamentId] = match;
          console.log(`[Tournaments] Found match for tournament ${tournamentId}:`, match);
        }
        // If match is null, don't add it to the map - undefined means "not checked yet"
      });
      console.log(`[Tournaments] All current matches:`, matchesMap);
      setCurrentMatches(matchesMap);

      // Refresh past count in the background
      fetchPastCount();
    } catch (err: any) {
      setError(err.message || "Failed to load tournaments");
    } finally {

      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const fetchPastCount = async () => {
    try {
      const headers = await buildAuthHeaders();

      // Ask for a single past tournament so the API returns total_count
      const response = await fetch(`${API_BASE}/tournaments?category=past&limit=1`, {
        credentials: "include",
        headers,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch past tournament count");
      }

      const data = await response.json();
      if (typeof data.total_count === "number") {
        setPastCount(data.total_count);
      } else if (Array.isArray(data.tournaments)) {
        // Fallback: if total_count is missing, at least use the length we got
        setPastCount(data.tournaments.length);
      }
    } catch (err) {
      console.error("Failed to fetch past tournament count", err);
    }
  };


  const fetchPastTournaments = async (reset: boolean = false) => {
    if (pastLoading) return;
    try {
      setPastLoading(true);
      if (reset) {
        setPastError(null);
      }
      
      const headers = await buildAuthHeaders();
      
      const requestOffset = reset ? 0 : pastOffset;
      const response = await fetch(`${API_BASE}/tournaments?category=past&limit=${PAST_PAGE_SIZE}&offset=${requestOffset}`, {
        credentials: "include",
        headers,
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch past tournaments");
      }
      
      const data = await response.json();
      // Backend already filters to only tournaments the user entered, so no need to filter here
      // Removing the filter ensures tournaments load even if user_entry is temporarily missing
      const fetchedTournaments: Tournament[] = data.tournaments || [];
      setTournaments(prev => ({
        ...prev,
        past: reset ? fetchedTournaments : [...prev.past, ...fetchedTournaments],
      }));
      
      if (typeof data.total_count === "number") {
        setPastCount(data.total_count);
      } else {
        setPastCount(prev => {
          const base = reset ? 0 : (prev ?? 0);
          return base + fetchedTournaments.length;
        });
      }

      const nextOffsetValue = data.next_offset !== undefined ? data.next_offset : requestOffset + fetchedTournaments.length;
      const hasMore = data.has_more !== undefined ? data.has_more : (fetchedTournaments.length === PAST_PAGE_SIZE);
      setPastOffset(nextOffsetValue);
      setPastHasMore(hasMore);
      setPastLoaded(true);
    } catch (err: any) {
      setPastError(err.message || "Failed to load past tournaments");
    } finally {
      setPastLoading(false);
    }
  };

  useEffect(() => {
    fetchTournaments();
    
    const interval = setInterval(() => {
      fetchTournaments(false);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activeTab === "past" && !pastLoaded && !pastLoading) {
      fetchPastTournaments(true);
    }
  }, [activeTab, pastLoaded, pastLoading]);

  const handlePlayTournamentMatch = async (tournamentId: number, matchId: number) => {
    try {
      setError(null);
      
      // Get match details to find opponent
      const headers = await buildAuthHeaders();
      
      const matchResponse = await fetch(`${API_BASE}/tournaments/${tournamentId}/matches/${matchId}`, {
        credentials: "include",
        headers,
      });
      
      if (!matchResponse.ok) {
        if (matchResponse.status === 404) {
          // Match not found - might have been cancelled or completed
          setError("This match is no longer available. The tournament may have been cancelled or the match completed.");
          return;
        }
        throw new Error("Failed to get match details");
      }
      
      const matchData = await matchResponse.json();
      
      // Navigate to play page with tournament match context
      // The play page will handle creating the game with tournament_match_id
      navigate(`/play?tournament=${tournamentId}&match=${matchId}&opponent=${encodeURIComponent(matchData.opponent_username || 'Opponent')}`);
    } catch (err: any) {
      const errorMessage = err.message || "Failed to start tournament match";
      // Check for ALREADY_IN_GAME error
      if (errorMessage.includes('ALREADY_IN_GAME') || errorMessage.includes('already in')) {
        setError("You are already in a game. Please finish or leave that game before joining this tournament match.");
      } else {
        setError(errorMessage);
      }
    }
  };

  // Auto-start tournament matches when they become available
  useEffect(() => {
    // Only auto-start if we're on the tournaments page (not in a game)
    if (window.location.pathname !== '/tournaments') {
      return;
    }

    // Check each tournament for a new pending match
    Object.entries(currentMatches).forEach(([tournamentIdStr, match]: [string, any]) => {
      // Skip if match is null or undefined (eliminated users won't have matches)
      if (!match || !match.id) {
        return;
      }

      const tournamentId = parseInt(tournamentIdStr);
      const matchId = match.id;

      // Skip if we've already auto-started this match
      if (autoStartedMatches.has(matchId)) {
        return;
      }

      // Only auto-start if match is pending or in_progress
      if (match.status === 'pending' || match.status === 'in_progress') {
        console.log(`[Tournaments] Auto-starting tournament match ${matchId} for tournament ${tournamentId}`);
        
        // Mark as auto-started
        setAutoStartedMatches(prev => new Set(prev).add(matchId));
        
        // Auto-start the match (grace period is handled by backend)
        handlePlayTournamentMatch(tournamentId, matchId).catch(err => {
          console.error(`[Tournaments] Failed to auto-start match ${matchId}:`, err);
          // Remove from auto-started set so we can retry
          setAutoStartedMatches(prev => {
            const next = new Set(prev);
            next.delete(matchId);
            return next;
          });
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMatches, autoStartedMatches]);

  const handleEnterTournament = async (tournamentId: number) => {
    try {
      setEnteringTournament(tournamentId);
      setError(null);

      const headers = await buildAuthHeaders();

      const response = await fetch(`${API_BASE}/tournaments/${tournamentId}/enter`, {
        method: "POST",
        credentials: "include",
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        // Parse ALREADY_IN_TOURNAMENT error to extract tournament name
        if (errorText.includes('ALREADY_IN_TOURNAMENT')) {
          const match = errorText.match(/ALREADY_IN_TOURNAMENT:\s*(.+)/);
          const tournamentName = match ? match[1] : 'another tournament';
          throw new Error(`You are already registered in ${tournamentName}. You can only be in one tournament at a time.`);
        }
        // Parse guest account error
        if (errorText.includes('GUEST_ACCOUNTS_CANNOT_ENTER_TOURNAMENTS')) {
          const match = errorText.match(/GUEST_ACCOUNTS_CANNOT_ENTER_TOURNAMENTS:\s*(.+)/);
          const message = match ? match[1] : 'Complete registration to enter tournaments';
          throw new Error(message);
        }
        throw new Error(errorText || "Failed to enter tournament");
      }

      // Refresh tournaments and account
      await fetchTournaments();
      if (onAccountRefresh) {
        onAccountRefresh();
      }
    } catch (err: any) {
      const errorMessage = err.message || "Failed to enter tournament";
      setError(errorMessage);
    } finally {
      setEnteringTournament(null);
    }
  };

  const formatTierName = (tier: string) => {
    const tierNames: { [key: string]: string } = {
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly",
      high_roller: "High Roller",
      season_finale: "Season Finale",
    };
    return tierNames[tier] || tier;
  };

  const formatTournamentName = (tournament: Tournament): React.ReactNode => {
    const tierNames: { [key: string]: string } = {
      "daily": "Daily Tournament",
      "weekly": "Weekly Tournament",
      "monthly": "Monthly Championship",
      "high_roller": "High Roller Invitational",
      "season_finale": "Season Finale",
    };
    
    const tierName = tierNames[tournament.tier] || tournament.tier.charAt(0).toUpperCase() + tournament.tier.slice(1);
    
    if (!tournament.scheduled_start_time) {
      return tierName;
    }
    
    const startTime = new Date(tournament.scheduled_start_time);
    const dateStr = startTime.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
    const timeStr = startTime.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    }).toLowerCase();
    
    return (
      <>
        {tierName}:<br />
        {dateStr} - {timeStr}
      </>
    );
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return "TBD";
    const date = new Date(dateStr);
    // Display in user's local timezone with date and time
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  };
  
  const formatDateTimeShort = (dateStr: string | null) => {
    if (!dateStr) return "TBD";
    const date = new Date(dateStr);
    // Display in user's local timezone, shorter format
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isGuest = () => {
    return !account.email_verified || (account.email && account.email.endsWith('@guest.local'));
  };

  const canEnter = (tournament: Tournament) => {
    // Block guest accounts from entering tournaments
    if (isGuest()) return false;
    if (tournament.user_entry) return false; // Already entered
    if (tournament.status === "in_progress" || tournament.status === "completed" || tournament.status === "cancelled") return false;
    
    const now = new Date();
    
    // Check registration window - must be open
    const regOpen = tournament.registration_open_time ? new Date(tournament.registration_open_time) : null;
    const regClose = tournament.registration_close_time ? new Date(tournament.registration_close_time) : null;
    
    if (regOpen && now < regOpen) return false; // Registration hasn't opened yet
    if (regClose && now >= regClose) return false; // Registration has closed

    // Check gear requirement
    const totalGear = (account.equipment_charges || 0) + 
                      (account.purchased_gear_charges || 0) +
                      (account.tournament_won_gear || 0);
    if (totalGear < tournament.gear_cost) return false;

    // Check banked glint requirement
    const bankedGlint = parseFloat(account.banked_glint || "0");
    const requiredGlint = parseFloat(tournament.banked_glint_requirement);
    if (bankedGlint < requiredGlint) return false;

    // Check if tournament is full
    if (tournament.entry_count !== undefined && tournament.max_players !== undefined) {
      if (tournament.entry_count >= tournament.max_players) return false;
    }

    return true;
  };

  const getQualificationMessage = (tournament: Tournament) => {
    if (tournament.user_entry) {
      return "✓ You are entered";
    }

    // Show registration message for guests
    if (isGuest()) {
      return "Complete registration to enter";
    }

    const totalGear = (account.equipment_charges || 0) + 
                      (account.purchased_gear_charges || 0) +
                      (account.tournament_won_gear || 0);
    if (totalGear < tournament.gear_cost) {
      return `Need ${tournament.gear_cost - totalGear} more gear`;
    }

    const bankedGlint = parseFloat(account.banked_glint || "0");
    const requiredGlint = parseFloat(tournament.banked_glint_requirement);
    if (bankedGlint < requiredGlint) {
      return `Need ${(requiredGlint - bankedGlint).toFixed(2)} more banked glint`;
    }

    if (tournament.entry_count !== undefined && tournament.max_players !== undefined) {
      if (tournament.entry_count >= tournament.max_players) {
        return "Tournament full";
      }
    }

    const now = new Date();
    const regClose = tournament.registration_close_time ? new Date(tournament.registration_close_time) : null;
    if (regClose && now >= regClose) {
      return "Registration closed";
    }

    return "You qualify";
  };

  const canShowBracket = (tournament: Tournament) => {
    if (tournament.status !== "completed") return false;
    if (tournament.format_type && tournament.format_type !== "single_elimination") return false;
    return (tournament.entry_count || 0) >= 4;
  };

  const fetchTournamentBracket = async (tournamentId: number) => {
    setBracketLoading(true);
    setBracketError(null);
    setBracketData(null);

    try {
      const headers: HeadersInit = { "Content-Type": "application/json" };
      try {
        const { getValidGuestToken } = await import('../lib/guest-auth');
        const guestToken = await getValidGuestToken();
        if (guestToken) {
          headers["Authorization"] = `Bearer ${guestToken}`;
        }
      } catch {
        // Continue without token
      }

      const response = await fetch(`${API_BASE}/tournaments/${tournamentId}/bracket`, {
        credentials: "include",
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to load bracket");
      }

      const data = await response.json();
      setBracketData(data);
    } catch (err: any) {
      setBracketError(err.message || "Failed to load bracket");
    } finally {
      setBracketLoading(false);
    }
  };

  const openBracket = (tournament: Tournament) => {
    setSelectedBracketTournament(tournament);
    setBracketModalOpen(true);
    fetchTournamentBracket(tournament.id);
  };

  const closeBracket = () => {
    setBracketModalOpen(false);
    setBracketData(null);
    setBracketError(null);
    setBracketLoading(false);
    setSelectedBracketTournament(null);
  };

  if (loading) {
    return (
      <PageLayout account={account} onLogout={onLogout}>
        <div className="p-8 text-center">
          <p style={{ color: '#3e2615' }}>Loading tournaments...</p>
        </div>
      </PageLayout>
    );
  }

  const renderTournamentCard = (tournament: Tournament, isPast: boolean = false) => {
    // iPhone-specific font size reductions
    const cardFontSize = isIPhone ? (isMobileLandscape ? '10px' : '11px') : (isIOSPad && isMobileLandscape ? '16px' : (isMobileLandscape ? '12px' : '14px'));
    const cardTitleSize = isIPhone ? (isMobileLandscape ? '12px' : '16px') : (isIOSPad && isMobileLandscape ? '20px' : (isMobileLandscape ? '14px' : '20px'));
    // iPhone-specific padding and spacing reductions
    const cardPadding = isIPhone ? (isMobileLandscape ? 'p-1' : 'p-3') : (isMobileLandscape ? 'p-2' : 'p-6');
    const headerMarginBottom = isIPhone ? (isMobileLandscape ? 'mb-0.5' : 'mb-1') : (isMobileLandscape ? 'mb-2' : 'mb-3');
    const contentGap = isIPhone ? (isMobileLandscape ? '0.25rem' : '0.375rem') : (isMobileLandscape ? '0.5rem' : '0.75rem');
    const sectionPadding = isIPhone ? (isMobileLandscape ? '0.25rem' : '0.375rem') : (isMobileLandscape ? '0.5rem' : '0.625rem');
    const sectionGap = isIPhone ? (isMobileLandscape ? '0.1rem' : '0.15rem') : (isMobileLandscape ? '0.25rem' : '0.375rem');
    const qualificationMarginBottom = isIPhone ? (isMobileLandscape ? 'mb-0.5' : 'mb-1') : (isMobileLandscape ? 'mb-1.5' : 'mb-3');
    
    return (
      <div
        key={tournament.id}
        className={`rounded-2xl border-4 ${cardPadding}`}
        style={{
          background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
          borderColor: '#8b6542',
          position: 'relative'
        }}
      >
        {/* Header with title and tier badge in upper right */}
        <div className={`flex items-start justify-between ${headerMarginBottom}`}>
          <h2 className={`${isMobileLandscape ? 'text-sm' : 'text-xl'} font-bold flex-1 pr-2`} style={{ 
            color: '#3e2615',
            fontSize: cardTitleSize,
            lineHeight: '1.3'
          }}>
            {formatTournamentName(tournament)}
          </h2>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            {tournament.status === "cancelled" && (
              <span className={`${isMobileLandscape ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-1 text-xs'} font-semibold rounded`} style={{ 
                background: '#fee067', 
                color: '#3e2615',
                fontSize: isIOSPad && isMobileLandscape ? '12px' : undefined
              }}>
                CANCELLED
              </span>
            )}
            <span className={`${isMobileLandscape ? 'text-xs px-2 py-1' : 'text-sm px-2.5 py-1'} font-semibold rounded`} style={{ 
              background: '#fffaeb', 
              color: '#8b6542',
              border: '2px solid #8b6542',
              fontSize: isIOSPad && isMobileLandscape ? '14px' : undefined,
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}>
              {formatTierName(tournament.tier)}
            </span>
          </div>
        </div>

        {/* Main content area with better structure */}
        <div style={{ fontSize: cardFontSize, color: '#3e2615', display: 'flex', flexDirection: 'column', gap: contentGap }}>
          {/* Entry details section */}
          <div className="rounded-lg" style={{ 
            background: 'rgba(255, 250, 235, 0.6)', 
            border: '1px solid rgba(139, 101, 66, 0.3)',
            padding: sectionPadding
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: sectionGap }}>
              <div className="flex justify-between items-start">
                <span className="font-semibold" style={{ color: '#8b6542' }}>Entry Cost:</span>
                <span className="text-right font-medium">{tournament.gear_cost} Gear</span>
              </div>
              {parseFloat(tournament.banked_glint_requirement) > 0 && (
                <div className="flex justify-between items-start">
                  <span className="font-semibold" style={{ color: '#8b6542' }}>Bank Requirement:</span>
                  <span className="text-right font-medium">
                    ✧{parseFloat(tournament.banked_glint_requirement).toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-start">
                <span className="font-semibold" style={{ color: '#8b6542' }}>Players:</span>
                <span className="text-right font-medium">
                  {tournament.entry_count || 0} / {tournament.max_players || "∞"}
                </span>
              </div>
              {tournament.config.min_players && (
                <div className="flex justify-between items-start">
                  <span className="font-semibold" style={{ color: '#8b6542' }}>Min Required:</span>
                  <span className="text-right font-medium">
                    {tournament.config.min_players} players
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Schedule section (for active tournaments) */}
          {!isPast && (
            <div className="rounded-lg" style={{ 
              background: 'rgba(255, 250, 235, 0.6)', 
              border: '1px solid rgba(139, 101, 66, 0.3)',
              padding: sectionPadding
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: sectionGap }}>
                {tournament.registration_open_time && (
                  <div className="flex justify-between items-start">
                    <span className="font-semibold" style={{ color: '#8b6542' }}>Registration Opens:</span>
                    <span className="text-right font-medium text-xs">
                      {formatDateTime(tournament.registration_open_time)}
                    </span>
                  </div>
                )}
                {tournament.scheduled_start_time && (
                  <div className="flex justify-between items-start">
                    <span className="font-semibold" style={{ color: '#8b6542' }}>Start Time:</span>
                    <span className="text-right font-medium text-xs">
                      {formatDateTime(tournament.scheduled_start_time)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Results section (for past tournaments) */}
          {isPast && (
            <div className="rounded-lg" style={{ 
              background: 'rgba(255, 250, 235, 0.6)', 
              border: '1px solid rgba(139, 101, 66, 0.3)',
              padding: sectionPadding
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: sectionGap }}>
                <div className="flex justify-between items-start">
                  <span className="font-semibold" style={{ color: '#8b6542' }}>Status:</span>
                  <span className="text-right font-medium capitalize">{tournament.status}</span>
                </div>
                {tournament.user_entry?.final_placement && (
                  <div className="flex justify-between items-start">
                    <span className="font-semibold" style={{ color: '#8b6542' }}>Your Placement:</span>
                    <span className="text-right font-medium">
                      {tournament.user_entry.final_placement === 1 ? "🥇 1st" :
                       tournament.user_entry.final_placement === 2 ? "🥈 2nd" :
                       tournament.user_entry.final_placement === 3 ? "🥉 3rd" :
                       `${tournament.user_entry.final_placement}th`}
                    </span>
                  </div>
                )}
                {tournament.user_entry?.prizes && (
                  <div className="flex justify-between items-start">
                    <span className="font-semibold" style={{ color: '#8b6542' }}>Prizes Won:</span>
                    <span className="text-right font-medium">
                      {tournament.user_entry.prizes.glint && `✧${tournament.user_entry.prizes.glint}`}
                      {tournament.user_entry.prizes.glint && tournament.user_entry.prizes.gear && " + "}
                      {tournament.user_entry.prizes.gear && `${tournament.user_entry.prizes.gear} Gear`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* View bracket button - hidden until feature is ready
        {isPast && canShowBracket(tournament) && (
          <div className="mb-4">
            <button
              onClick={() => openBracket(tournament)}
              className="px-4 py-2 rounded-lg font-semibold transition-all"
              style={{
                background: '#fffaeb',
                color: '#3e2615',
                boxShadow: '0 2px 0 #8b6542, 0 4px 8px rgba(0,0,0,0.2)',
                border: '2px solid #8b6542'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              View bracket
            </button>
          </div>
        )}
        */}

        {!isPast && (
          <>
            {/* Qualification message */}
            <div className={qualificationMarginBottom}>
              <div style={{ 
                color: '#3e2615', 
                opacity: 0.8,
                fontSize: isIOSPad && isMobileLandscape ? '14px' : (isMobileLandscape ? '10px' : '12px')
              }}>
                {getQualificationMessage(tournament)}
              </div>
            </div>

            {/* Prizes and Enter button side-by-side in two columns */}
            <div className={`grid ${isMobileLandscape ? 'grid-cols-2 gap-1.5' : 'grid-cols-2 gap-3'}`}>
              {/* Left column: Prizes */}
              <div>
                {(tournament.current_prize_table || tournament.config.prize_table) ? (
                  <div className={`${isMobileLandscape ? 'p-1.5' : 'p-3'} rounded-lg h-full`} style={{ 
                    background: '#fffaeb', 
                    border: '2px solid #8b6542'
                  }}>
                    <div className="font-semibold" style={{ 
                      color: '#3e2615',
                      fontSize: isIOSPad && isMobileLandscape ? '15px' : undefined,
                      marginBottom: isMobileLandscape ? '0.25rem' : '0.5rem'
                    }}>
                      Prizes:
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: isMobileLandscape ? '0.125rem' : '0.25rem',
                      fontSize: isIOSPad && isMobileLandscape ? '14px' : (isMobileLandscape ? '10px' : '12px')
                    }}>
                      {Object.entries(tournament.current_prize_table || tournament.config.prize_table || {})
                        .sort(([a], [b]) => parseInt(a) - parseInt(b))
                        .slice(0, 3)
                        .map(([placement, prize]) => (
                          <div key={placement} className="flex justify-between items-center">
                            <span className="font-medium">
                              {placement === "1" ? "🥇" : placement === "2" ? "🥈" : placement === "3" ? "🥉" : `${placement}th`}:
                            </span>
                            <span className="font-semibold">
                              {prize.glint && `✧${prize.glint}`}
                              {prize.glint && prize.gear && " + "}
                              {prize.gear && `${prize.gear} Gear`}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                ) : (
                  <div></div>
                )}
              </div>

              {/* Right column: Enter button (two rows) */}
              <div className="flex flex-col">
                {tournament.status === "cancelled" && tournament.user_entry ? (
                  <div className={`${isMobileLandscape ? 'p-1.5' : 'p-3'} rounded-lg h-full flex items-center justify-center`} style={{ background: '#fee067', border: '2px solid #8b6542' }}>
                    <div className="text-center">
                      <div className={`${isMobileLandscape ? 'text-[10px]' : 'text-sm'} font-semibold ${isMobileLandscape ? 'mb-0.5' : 'mb-1'}`} style={{ 
                        color: '#3e2615',
                        fontSize: isIOSPad && isMobileLandscape ? '14px' : undefined
                      }}>
                        ⚠️ Cancelled
                      </div>
                      <div className={isMobileLandscape ? 'text-[9px]' : 'text-xs'} style={{ 
                        color: '#3e2615',
                        fontSize: isIOSPad && isMobileLandscape ? '12px' : undefined
                      }}>
                        Refunded
                      </div>
                    </div>
                  </div>
                ) : tournament.status === "in_progress" ? (
                  tournament.user_entry ? (
                    tournament.user_entry.status === "eliminated" ? (
                      <button
                        disabled
                        className={`w-full h-full ${isMobileLandscape ? 'px-1.5 py-1.5 text-[10px]' : 'px-4 py-3'} rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center`}
                        style={{
                          background: '#d4a05f',
                          color: '#3e2615',
                          border: '2px solid #8b6542',
                          fontSize: isIOSPad && isMobileLandscape ? '14px' : undefined
                        }}
                      >
                        <span>In</span>
                        <span>Progress</span>
                      </button>
                    ) : currentMatches[tournament.id] ? (
                      <button
                        onClick={() => {
                          const match = currentMatches[tournament.id];
                          if (match && match.id) {
                            handlePlayTournamentMatch(tournament.id, match.id);
                          }
                        }}
                        className={`w-full h-full ${isMobileLandscape ? 'px-1.5 py-1.5 text-[10px]' : 'px-4 py-3'} rounded-lg font-semibold transition-all flex flex-col items-center justify-center`}
                        style={{
                          background: '#fffaeb',
                          color: '#3e2615',
                          boxShadow: '0 2px 0 #8b6542, 0 4px 8px rgba(0,0,0,0.2)',
                          border: '2px solid #8b6542',
                          fontSize: isIOSPad && isMobileLandscape ? '14px' : undefined
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <span>🎮 Play</span>
                        <span>Match</span>
                      </button>
                    ) : (
                      <button
                        disabled
                        className={`w-full h-full ${isMobileLandscape ? 'px-1.5 py-1.5 text-[10px]' : 'px-4 py-3'} rounded-lg font-semibold transition-all disabled:opacity-70 disabled:cursor-not-allowed flex flex-col items-center justify-center`}
                        style={{
                          background: '#fffaeb',
                          color: '#3e2615',
                          boxShadow: '0 2px 0 #8b6542, 0 4px 8px rgba(0,0,0,0.2)',
                          border: '2px solid #8b6542',
                          fontSize: isIOSPad && isMobileLandscape ? '14px' : undefined
                        }}
                      >
                        <span>Preparing</span>
                        <span>match...</span>
                      </button>
                    )
                  ) : (
                    <button
                      disabled
                      className={`w-full h-full ${isMobileLandscape ? 'px-1.5 py-1.5 text-[10px]' : 'px-4 py-3'} rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center`}
                      style={{
                        background: '#d4a05f',
                        color: '#3e2615',
                        border: '2px solid #8b6542'
                      }}
                    >
                      <span>In</span>
                      <span>Progress</span>
                    </button>
                  )
                ) : (
                  <button
                    onClick={() => handleEnterTournament(tournament.id)}
                    disabled={!canEnter(tournament) || enteringTournament === tournament.id || tournament.status === "in_progress"}
                    title={isGuest() && !tournament.user_entry ? "Complete registration" : undefined}
                    className={`w-full h-full ${isMobileLandscape ? 'px-1.5 py-1.5 text-[10px]' : 'px-4 py-3'} rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center`}
                    style={{
                      background: canEnter(tournament) ? '#fffaeb' : '#d4a05f',
                      color: '#3e2615',
                      boxShadow: canEnter(tournament) ? '0 2px 0 #8b6542, 0 4px 8px rgba(0,0,0,0.2)' : 'none',
                      border: '2px solid #8b6542',
                      fontSize: isIOSPad && isMobileLandscape ? '14px' : undefined
                    }}
                    onMouseEnter={(e) => {
                      if (canEnter(tournament) && !e.currentTarget.disabled) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {enteringTournament === tournament.id ? (
                      <>
                        <span>Entering</span>
                        <span>...</span>
                      </>
                    ) : tournament.user_entry ? (
                      <>
                        <span>Entered</span>
                        <span>✓</span>
                      </>

                    ) : canEnter(tournament) ? (
                      <>

                        <span>Enter</span>
                        <span>Tournament</span>
                      </>
                    ) : (
                      <>
                        <span>Cannot</span>
                        <span>Enter</span>
                      </>
                    )}
                  </button>
                )}

            {/* close any wrappers that were opened above the button area */}
          </div>
        </div>

          </>
        )}

        {/* Show prizes for past tournaments */}
        {isPast && (tournament.current_prize_table || tournament.config.prize_table) && (
          <div className={`${isMobileLandscape ? 'mt-2 p-2' : 'mt-3 p-2.5'} rounded-lg`} style={{ 
            background: '#fffaeb', 
            border: '2px solid #8b6542'
          }}>
            <div className="font-semibold" style={{ 
              color: '#3e2615',
              fontSize: isIOSPad && isMobileLandscape ? '15px' : undefined,
              marginBottom: isMobileLandscape ? '0.25rem' : '0.5rem'
            }}>
              Prizes:
            </div>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: isMobileLandscape ? '0.125rem' : '0.25rem',
              fontSize: isIOSPad && isMobileLandscape ? '14px' : (isMobileLandscape ? '12px' : '14px')
            }}>
              {Object.entries(tournament.current_prize_table || tournament.config.prize_table || {})
                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                .slice(0, 3)
                .map(([placement, prize]) => (
                  <div key={placement} className="flex justify-between items-center">
                    <span className="font-medium">
                      {placement === "1" ? "🥇" : placement === "2" ? "🥈" : placement === "3" ? "🥉" : `${placement}th`}:
                    </span>
                    <span className="font-semibold">
                      {prize.glint && `✧${prize.glint}`}
                      {prize.glint && prize.gear && " + "}
                      {prize.gear && `${prize.gear} Gear`}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <PageLayout account={account} onLogout={onLogout}>
      {/* Native full-frame background */}
      {(isAndroid || isIOSPad || isIPhone) && (
        <div
          className="fixed inset-0 w-screen h-screen overflow-hidden"
          style={{ zIndex: 0, pointerEvents: "none" }}
          aria-hidden="true"
        >
          <img
            src={getAssetPath(backgroundSrc)}
            alt=""
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: "cover", objectPosition: "center" }}
            draggable={false}
          />
        </div>
      )}
      {androidTextStyle}
      <div 
        ref={contentRef}
        className={`${isMobileLandscape ? "fixed z-40 p-2 flex flex-col" : "max-w-6xl mx-auto p-4 md:p-6"} ${isAndroidTablet ? "android-tablet-text-tournaments" : ""}`}
        style={isMobileLandscape ? (
          isIOSPad ? {
            top: isAndroidTablet
              ? `calc(32px - 8px + ${androidMenuOffset}px)`
              : (isAndroid ? 'calc(32px - 8px)' : `calc(env(safe-area-inset-top, 0px) - 8px + ${iosTopPad}px + ${iosPadGutterY / 2 + iosPadShiftY}px)`),
            left: isAndroid
              ? `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${androidInsetLeft}px)`
              : `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${iosPadGutterX}px)`,
            right: isAndroid
              ? `calc(env(safe-area-inset-right, 0px) + 48px + ${androidInsetRight}px)`
              : `calc(env(safe-area-inset-right, 0px) + 48px + ${iosPadGutterX}px)`,
            bottom: scrollReduction
              ? `calc(env(safe-area-inset-bottom, 8px) - 14px + 50px + ${scrollReduction}px + ${iosBottomPad}px)`
              : `calc(env(safe-area-inset-bottom, 8px) - 14px + 50px + ${iosBottomPad}px + ${iosPadGutterY / 2}px)`,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            marginLeft: "-20px",
            marginRight: 0,
            fontSize: textScale > 1 ? `${textScale}em` : undefined,
            lineHeight: textScale > 1 ? 1.2 : undefined,
            transform: (androidContentShift || iosPadShiftX) ? `translateX(${(androidContentShift || 0) + iosPadShiftX}px)` : undefined,
            ...(androidBodyFontSize ? { fontSize: androidBodyFontSize, lineHeight: androidBodyLineHeight } : {}),
          } : {
            top: isAndroidTablet
              ? `calc(32px - 8px + ${androidMenuOffset}px)`
              : (isAndroid ? 'calc(32px - 8px)' : (isIPhone ? 'calc(32px - 8px)' : 'calc(env(safe-area-inset-top, 0px) - 8px)')),
            left: isAndroid
              ? `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${androidInsetLeft}px)`
              : isIPhone
              ? `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${iphoneRightShift}px)`
              : 'calc(env(safe-area-inset-left, 0px) + 32px + 112px)',
            right: isAndroid
              ? `calc(env(safe-area-inset-right, 0px) + 48px + ${androidInsetRight}px)`
              : isIPhone
              ? `calc(env(safe-area-inset-right, 0px) + 48px + ${iphoneRightNarrow}px)`
              : 'calc(env(safe-area-inset-right, 0px) + 48px)',
            bottom: scrollReduction
              ? `calc(env(safe-area-inset-bottom, 8px) - 14px + 50px + ${scrollReduction}px)`
              : 'calc(env(safe-area-inset-bottom, 8px) - 14px + 50px)',
            overflow: "visible",
            display: "flex",
            flexDirection: "column",
            marginLeft: "-20px",
            marginRight: 0,
            fontSize: textScale > 1 ? `${textScale}em` : undefined,
            lineHeight: textScale > 1 ? 1.2 : undefined,
            transform: androidContentShift ? `translateX(${androidContentShift}px)` : undefined,
            ...(androidBodyFontSize ? { fontSize: androidBodyFontSize, lineHeight: androidBodyLineHeight } : {}),
          }
        ) : (isAndroid ? { paddingLeft: `${androidInsetLeft}px`, paddingRight: `${androidInsetRight}px`, fontSize: textScale > 1 ? `${textScale}em` : undefined, lineHeight: textScale > 1 ? 1.2 : undefined } : (textScale > 1 ? { fontSize: `${textScale}em`, lineHeight: 1.2 } : {}))}
      >
        {!isMobileLandscape && (
          <h1 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#3e2615' }}>
            Tournaments
          </h1>
        )}

        {error && (
          <div className={`${isMobileLandscape ? 'mb-2 p-2' : 'mb-4 p-4'} rounded-lg flex items-center justify-between`} style={{ background: '#fee2e2', border: '2px solid #ef4444' }}>
            <p className={isMobileLandscape ? 'text-xs' : ''} style={{ color: '#991b1b' }}>{error}</p>
            <button
              onClick={() => setError(null)}
              className={`${isMobileLandscape ? 'ml-2 px-2 py-1 text-xs' : 'ml-4 px-3 py-1 text-sm'} rounded font-semibold`}
              style={{ background: '#991b1b', color: '#fee2e2' }}
            >
              Dismiss
            </button>
          </div>
        )}

        <div
          className={`rounded-2xl border-4 shadow-xl ${isMobileLandscape ? "p-3 flex-1 flex flex-col" : "px-4 py-4 md:px-6 md:py-6"}`}
          style={isMobileLandscape ? {
            background: "#fffaeb",
            borderColor: "#8b6542",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)',
          } : {
            background: "#fffaeb",
            borderColor: "#8b6542",
          }}
        >
          {/* Tabs */}
          <div className={`${isMobileLandscape ? 'mb-2' : 'mb-6'} flex gap-2 border-b-2`} style={{ borderColor: '#8b6542' }}>
            <button
              onClick={() => setActiveTab("active")}
              className={`${isMobileLandscape ? 'px-2 py-2 text-sm' : 'px-6 py-3 text-lg'} font-semibold transition-all flex-1`}
              style={{
                color: activeTab === "active" ? '#3e2615' : '#5a3b22',
                borderBottom: activeTab === "active" ? `3px solid #3e2615` : '3px solid transparent',
                background: 'transparent',
                fontSize: isIPhone ? (isMobileLandscape ? '13px' : '15px') : (isAndroidTablet ? '18px' : (isMobileLandscape ? '16px' : '18px')),
              }}
            >
              Active ({tournaments.active.length})
            </button>
            <button
              onClick={() => setActiveTab("upcoming")}
              className={`${isMobileLandscape ? 'px-2 py-2 text-sm' : 'px-6 py-3 text-lg'} font-semibold transition-all flex-1`}
              style={{
                color: activeTab === "upcoming" ? '#3e2615' : '#5a3b22',
                borderBottom: activeTab === "upcoming" ? `3px solid #3e2615` : '3px solid transparent',
                fontSize: isIPhone ? (isMobileLandscape ? '13px' : '15px') : (isAndroidTablet ? '18px' : (isMobileLandscape ? '16px' : '18px')),
              }}
            >
              Upcoming ({tournaments.upcoming.length})
            </button>
            <button
              onClick={() => setActiveTab("past")}
              className={`${isMobileLandscape ? 'px-2 py-2 text-sm' : 'px-6 py-3 text-lg'} font-semibold transition-all flex-1`}
              style={{
                color: activeTab === "past" ? '#3e2615' : '#5a3b22',
                borderBottom: activeTab === "past" ? `3px solid #3e2615` : '3px solid transparent',
                fontSize: isIPhone ? (isMobileLandscape ? '13px' : '15px') : (isAndroidTablet ? '18px' : (isMobileLandscape ? '16px' : '18px')),
              }}
            >
              Completed ({pastCount ?? tournaments.past.length})
            </button>
          </div>

          <div
            className={isMobileLandscape ? "relative flex-1 min-h-0" : ""}
            style={isMobileLandscape
              ? (isIOSPad
                ? { overflow: 'visible', display: 'flex', flexDirection: 'column' }
                : { overflow: 'visible', display: 'flex', flexDirection: 'column' })
              : {}}
          >
            <div
              ref={scrollRef}
              onScroll={(e) => updateScrollIndicators(e.currentTarget)}
              className={isMobileLandscape && isIOSPad ? "h-full overflow-y-auto overflow-x-auto pr-1 flex-1" : (isMobileLandscape ? "h-full overflow-y-auto overflow-x-auto pr-1" : "")}
            style={isMobileLandscape && isIOSPad ? { paddingRight: '4px', minHeight: 0 } : (isMobileLandscape ? { paddingRight: '4px', minHeight: 0 } : {})}
            >
            {/* Active Tournaments */}
            {activeTab === "active" && (
              <>
                {tournaments.active.length > 0 ? (
                  <div className={`grid ${isMobileLandscape ? 'grid-cols-2 gap-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'}`}>
                    {tournaments.active.map((tournament) => renderTournamentCard(tournament, false))}
                  </div>
                ) : (
                  <div className={`rounded-2xl border-4 ${isMobileLandscape ? 'p-4' : 'p-8'} text-center`} style={{
                    background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
                    borderColor: '#8b6542'
                  }}>
                    <p className={isMobileLandscape ? 'text-sm' : ''} style={{ color: '#3e2615' }}>No active tournaments at this time.</p>
                  </div>
                )}
              </>
            )}

        {/* Upcoming Tournaments */}
        {activeTab === "upcoming" && (
          <>
            {tournaments.upcoming.length === 0 ? (
              <div className={`rounded-2xl border-4 ${isMobileLandscape ? 'p-4' : 'p-8'} text-center`} style={{
                background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
                borderColor: '#8b6542'
              }}>
                <p className={isMobileLandscape ? 'text-sm' : ''} style={{ color: '#3e2615' }}>No upcoming tournaments scheduled. Check back soon!</p>
              </div>
            ) : (
              <div className={`grid ${isMobileLandscape ? 'grid-cols-2 gap-2' : 'space-y-4'}`}>
                {tournaments.upcoming.map((tournament) => {
                  const startTime = tournament.scheduled_start_time ? new Date(tournament.scheduled_start_time) : null;
                  const regOpenTime = tournament.registration_open_time ? new Date(tournament.registration_open_time) : null;
                  const regCloseTime = tournament.registration_close_time ? new Date(tournament.registration_close_time) : null;
                  const now = new Date();
                  
                  const isRegOpen = regOpenTime && now >= regOpenTime && regCloseTime && now < regCloseTime;
                  const isRegSoon = regOpenTime && regOpenTime > now;
                  
                  const isIOSPad = /iPad/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                  const cardFontSize = isIOSPad && isMobileLandscape ? '16px' : (isMobileLandscape ? '12px' : '14px');
                  const cardTitleSize = isIOSPad && isMobileLandscape ? '20px' : (isMobileLandscape ? '16px' : '20px');
                  const cardSpacing = isIOSPad && isMobileLandscape ? '0.75rem' : (isMobileLandscape ? '0.5rem' : '0.75rem');
                  
                  // iPhone-specific spacing and font size for upcoming tournaments
                  const upcomingCardFontSize = isIPhone ? (isMobileLandscape ? '10px' : '11px') : cardFontSize;
                  const upcomingCardTitleSize = isIPhone ? (isMobileLandscape ? '12px' : '16px') : cardTitleSize;
                  const upcomingCardPadding = isIPhone ? (isMobileLandscape ? 'p-1' : 'p-3') : (isMobileLandscape ? 'p-3' : 'p-6');
                  const upcomingHeaderMarginBottom = isIPhone ? (isMobileLandscape ? 'mb-0.5' : 'mb-1') : (isMobileLandscape ? 'mb-2' : 'mb-3');
                  const upcomingContentGap = isIPhone ? (isMobileLandscape ? '0.25rem' : '0.375rem') : (isMobileLandscape ? '0.5rem' : '0.75rem');
                  const upcomingSectionPadding = isIPhone ? (isMobileLandscape ? '0.25rem' : '0.375rem') : (isMobileLandscape ? '0.5rem' : '0.625rem');
                  const upcomingSectionGap = isIPhone ? (isMobileLandscape ? '0.1rem' : '0.15rem') : (isMobileLandscape ? '0.25rem' : '0.375rem');
                  
                  return (
                    <div
                      key={tournament.id}
                      className={`rounded-2xl border-4 ${upcomingCardPadding}`}
                      style={{
                        background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
                        borderColor: '#8b6542',
                        position: 'relative'
                      }}
                    >
                      {/* Header with title and tier badge in upper right */}
                      <div className={`flex items-start justify-between ${upcomingHeaderMarginBottom}`}>
                        <h3 className={`${isMobileLandscape ? 'text-base' : 'text-xl'} font-bold flex-1 pr-2`} style={{ 
                          color: '#3e2615',
                          fontSize: upcomingCardTitleSize,
                          lineHeight: '1.3'
                        }}>
                          {formatTournamentName(tournament)}
                        </h3>
                        <span className={`${isMobileLandscape ? 'text-xs px-2 py-1' : 'text-sm px-2.5 py-1'} font-semibold rounded flex-shrink-0`} style={{ 
                          background: '#fffaeb', 
                          color: '#8b6542',
                          border: '2px solid #8b6542',
                          fontSize: isIOSPad && isMobileLandscape ? '14px' : undefined,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}>
                          {formatTierName(tournament.tier)}
                        </span>
                      </div>
                      
                      {/* Main content area with better structure */}
                      <div style={{ fontSize: upcomingCardFontSize, color: '#3e2615', display: 'flex', flexDirection: 'column', gap: upcomingContentGap }}>
                        {/* Schedule section */}
                        <div className="rounded-lg" style={{ 
                          background: 'rgba(255, 250, 235, 0.6)', 
                          border: '1px solid rgba(139, 101, 66, 0.3)',
                          padding: upcomingSectionPadding
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: upcomingSectionGap }}>
                            <div className="flex justify-between items-start">
                              <span className="font-semibold" style={{ color: '#8b6542' }}>Start Time:</span>
                              <span className="text-right font-medium">{formatDateTimeShort(tournament.scheduled_start_time)}</span>
                            </div>
                            {regOpenTime && (
                              <div className="flex justify-between items-start">
                                <span className="font-semibold" style={{ color: '#8b6542' }}>Registration:</span>
                                <span className="text-right font-medium">
                                  {isRegOpen ? (
                                    <span style={{ color: '#059669', fontWeight: 'bold' }}>OPEN NOW</span>
                                  ) : isRegSoon ? (
                                    `Opens ${formatDateTimeShort(tournament.registration_open_time)}`
                                  ) : (
                                    `${formatDateTimeShort(tournament.registration_open_time)} - ${formatDateTimeShort(tournament.registration_close_time)}`
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Entry details section */}
                        <div className="rounded-lg" style={{ 
                          background: 'rgba(255, 250, 235, 0.6)', 
                          border: '1px solid rgba(139, 101, 66, 0.3)',
                          padding: upcomingSectionPadding
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: upcomingSectionGap }}>
                            <div className="flex justify-between items-start">
                              <span className="font-semibold" style={{ color: '#8b6542' }}>Entry Cost:</span>
                              <span className="text-right font-medium">
                                {tournament.gear_cost} Gear
                                {parseFloat(tournament.banked_glint_requirement) > 0 && (
                                  <span> + ✧{parseFloat(tournament.banked_glint_requirement).toFixed(2)}</span>
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between items-start">
                              <span className="font-semibold" style={{ color: '#8b6542' }}>Format:</span>
                              <span className="text-right font-medium capitalize">{tournament.format_type.replace('_', ' ')}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Prizes section */}
                        {(tournament.current_prize_table || tournament.config.prize_table) && (
                          <div className="rounded-lg" style={{ 
                            background: '#fffaeb', 
                            border: '2px solid #8b6542',
                            padding: upcomingSectionPadding
                          }}>
                            <div className="font-semibold" style={{ 
                              color: '#3e2615', 
                              fontSize: isIOSPad && isMobileLandscape ? '15px' : undefined,
                              marginBottom: isMobileLandscape ? '0.25rem' : '0.375rem'
                            }}>
                              Prizes:
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobileLandscape ? '0.125rem' : '0.25rem' }}>
                              {Object.entries(tournament.current_prize_table || tournament.config.prize_table || {})
                                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                                .slice(0, 3)
                                .map(([placement, prize]) => (
                                  <div key={placement} className="flex justify-between items-center">
                                    <span className="font-medium">
                                      {placement === "1" ? "🥇" : placement === "2" ? "🥈" : placement === "3" ? "🥉" : `${placement}th`}:
                                    </span>
                                    <span className="font-semibold">
                                      {prize.glint && `✧${prize.glint}`}
                                      {prize.glint && prize.gear && " + "}
                                      {prize.gear && `${prize.gear} Gear`}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Action section */}
                      <div className={`${isMobileLandscape ? 'mt-2' : 'mt-3'} flex flex-col items-stretch gap-2`}>
                        {isRegOpen && (
                          <button
                            onClick={() => handleEnterTournament(tournament.id)}
                            disabled={!canEnter(tournament) || enteringTournament === tournament.id}
                            title={
                              isGuest() && !tournament.user_entry
                                ? "Complete registration"
                                : undefined
                            }
                            className={`${
                              isMobileLandscape
                                ? "px-3 py-2 text-xs"
                                : "px-6 py-3 text-sm"
                            } rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                            style={{
                              background: canEnter(tournament) ? '#fffaeb' : '#d4a05f',
                              color: '#3e2615',
                              boxShadow: canEnter(tournament) ? '0 2px 0 #8b6542, 0 4px 8px rgba(0,0,0,0.2)' : 'none',
                              border: '2px solid #8b6542',
                              fontSize: isIOSPad && isMobileLandscape ? '16px' : undefined,
                            }}
                            onMouseEnter={(e) => {
                              if (canEnter(tournament) && !e.currentTarget.disabled) {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            {enteringTournament === tournament.id
                              ? "Entering..."
                              : tournament.user_entry
                              ? "Entered ✓"
                              : "Enter Now"}
                          </button>
                        )}
                        {!isRegOpen && (
                          <div className={`${isMobileLandscape ? 'text-xs' : 'text-sm'} text-center py-2`} style={{ color: '#8b6542' }}>
                            {isRegSoon ? (
                              <div>
                                <div className="font-semibold">Registration Opens:</div>
                                <div>{formatDateTimeShort(tournament.registration_open_time)}</div>
                              </div>
                            ) : (
                              <div className="font-semibold">Registration Closed</div>
                            )}
                          </div>
                        )}
                        {tournament.user_entry && (
                          <div className={`${isMobileLandscape ? 'text-xs' : 'text-sm'} font-semibold text-center`} style={{ color: '#059669' }}>
                            ✓ You are entered
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Completed Tournaments */}
        {activeTab === "past" && (
          <>
            {pastError && (
              <div className={`${isMobileLandscape ? 'mb-2 p-2' : 'mb-4 p-4'} rounded-lg`} style={{ background: '#fee2e2', border: '2px solid #ef4444' }}>
                <p className={isMobileLandscape ? 'text-xs' : ''} style={{ color: '#991b1b' }}>{pastError}</p>
              </div>
            )}
            {tournaments.past.length === 0 && !pastLoading ? (
              <div className={`rounded-2xl border-4 ${isMobileLandscape ? 'p-4' : 'p-8'} text-center`} style={{
                background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
                borderColor: '#8b6542'
              }}>
                <p className={isMobileLandscape ? 'text-sm' : ''} style={{ color: '#3e2615' }}>No completed tournaments. Start playing to see your tournament history!</p>
              </div>
            ) : (
              <div className={`grid ${isMobileLandscape ? 'grid-cols-2 gap-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'}`}>
                {tournaments.past.map((tournament) => renderTournamentCard(tournament, true))}
              </div>
            )}
            {pastLoading && (
              <div className={`${isMobileLandscape ? 'mt-2' : 'mt-4'} text-center ${isMobileLandscape ? 'text-xs' : ''}`} style={{ color: '#3e2615' }}>
                Loading completed tournaments...
              </div>
            )}
            {!pastLoading && pastHasMore && (
              <div className={`${isMobileLandscape ? 'mt-2' : 'mt-4'} text-center`}>
                <button
                  onClick={() => fetchPastTournaments(false)}
                  className={`${isMobileLandscape ? 'px-3 py-1.5 text-xs' : 'px-4 py-2'} rounded-lg font-semibold transition-all`}
                  style={{
                    background: '#fffaeb',
                    color: '#3e2615',
                    border: '2px solid #8b6542',
                    boxShadow: '0 2px 0 #8b6542'
                  }}
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}
            </div>
            {isMobileLandscape && (
              <>
                {showTopIndicator && (
                  <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 flex justify-center" style={{ top: '0px', zIndex: 45 }}>
                    <svg width={28} height={18} viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.65))' }}>
                      <path d="M1 7L6 2L11 7" stroke="#f7b267" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
                {showBottomIndicator && (
                  <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 flex justify-center" style={{ bottom: '-20px', zIndex: 45 }}>
                    <svg width={28} height={18} viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.65))' }}>
                      <path d="M1 1L6 6L11 1" stroke="#f7b267" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <TournamentBracketModal
        isOpen={bracketModalOpen}
        onClose={closeBracket}
        tournamentName={selectedBracketTournament ? formatTournamentName(selectedBracketTournament) : undefined}
        bracketData={bracketData}
        loading={bracketLoading}
        error={bracketError}
        onRetry={() => selectedBracketTournament && fetchTournamentBracket(selectedBracketTournament.id)}
        currentUserId={account?.id}
      />
    </PageLayout>
  );
}
